import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PerfectDocumentSearch from '../services/perfectDocumentSearch';

const { FiCalendar, FiGlobe, FiFileText, FiDatabase, FiSearch } = FiIcons;

const FilterPanel = ({ filters, onFilterChange }) => {
  const handleFilterUpdate = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const handleArrayFilterUpdate = (key, value, checked) => {
    const currentArray = filters[key] || [];
    let newArray;
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray.filter(item => item !== value);
    }
    onFilterChange({ ...filters, [key]: newArray });
  };

  // Opciones de idioma
  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'Inglés' },
    { code: 'pt', name: 'Portugués' },
    { code: 'fr', name: 'Francés' },
    { code: 'de', name: 'Alemán' },
    { code: 'it', name: 'Italiano' },
    { code: 'zh', name: 'Chino' },
    { code: 'ja', name: 'Japonés' },
    { code: 'ko', name: 'Coreano' },
    { code: 'ru', name: 'Ruso' }
  ];

  // Tipos de documentos
  const documentTypes = [
    { code: 'article', name: 'Artículos de revista' },
    { code: 'preprint', name: 'Preprints' },
    { code: 'book', name: 'Libros' },
    { code: 'chapter', name: 'Capítulos de libro' },
    { code: 'thesis', name: 'Tesis doctorales' },
    { code: 'conference', name: 'Actas de conferencia' },
    { code: 'review', name: 'Artículos de revisión' },
    { code: 'report', name: 'Informes técnicos' }
  ];

  // Obtener fuentes y campos de búsqueda del servicio auténtico
  const sources = PerfectDocumentSearch.getSourceOptions();
  const searchFields = PerfectDocumentSearch.getSearchFieldOptions();

  const sortOptions = [
    { value: 'relevance', label: 'Relevancia' },
    { value: 'year_desc', label: 'Más reciente' },
    { value: 'year_asc', label: 'Más antiguo' },
    { value: 'citations', label: 'Más citado' }
  ];

  // Agrupar fuentes por tipo
  const groupedSources = sources.reduce((acc, source) => {
    const type = source.type || 'external';
    if (!acc[type]) acc[type] = [];
    acc[type].push(source);
    return acc;
  }, {});

  const typeLabels = {
    academic: '🔬 Fuentes Académicas Directas',
    preprint: '📄 Servidores de Preprints',
    biomedical: '🏥 Bases Biomédicas',
    general: '🎓 Búsquedas Generales',
    regional: '🌎 Fuentes Regionales',
    open_access: '📖 Acceso Abierto',
    educational: '📚 Fuentes Educativas',
    ai_academic: '🤖 Inteligencia Académica',
    discovery: '🔍 Herramientas de Descubrimiento',
    specialized: '⚡ Fuentes Especializadas',
    repository: '🏛️ Repositorios',
    ai: '🧠 Inteligencia Artificial',
    commercial: '💼 Fuentes Comerciales'
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 space-y-6"
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        Filtros de búsqueda
      </h3>

      {/* Campo de búsqueda */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Campo de búsqueda
          </label>
        </div>
        <select
          value={filters.searchField || 'all'}
          onChange={(e) => handleFilterUpdate('searchField', e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          {searchFields.map((field) => (
            <option key={field.code} value={field.code}>
              {field.name}
            </option>
          ))}
        </select>
      </div>

      {/* Rango de años */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Año de publicación
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input
            type="number"
            placeholder="Desde"
            value={filters.yearFrom}
            onChange={(e) => handleFilterUpdate('yearFrom', e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          <input
            type="number"
            placeholder="Hasta"
            value={filters.yearTo}
            onChange={(e) => handleFilterUpdate('yearTo', e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>
      </div>

      {/* Idiomas */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiGlobe} className="w-4 h-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Idioma
          </label>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {languages.map((lang) => (
            <label key={lang.code} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.languages?.includes(lang.code) || false}
                onChange={(e) => handleArrayFilterUpdate('languages', lang.code, e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {lang.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Tipos de documentos */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFileText} className="w-4 h-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Tipo de documento
          </label>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {documentTypes.map((type) => (
            <label key={type.code} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.documentTypes?.includes(type.code) || false}
                onChange={(e) => handleArrayFilterUpdate('documentTypes', type.code, e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {type.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Fuentes - Organizadas por tipo */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiDatabase} className="w-4 h-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark: text-gray-300">
            Fuentes académicas ({sources.length})
          </label>
        </div>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(groupedSources).map(([type, typeSources]) => (
            <div key={type} className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {typeLabels[type] || type}
              </h4>
              {typeSources.map((source) => (
                <label key={source.code} className="flex items-center space-x-2 pl-2">
                  <input
                    type="checkbox"
                    checked={filters.sources?.includes(source.code) || false}
                    onChange={(e) => handleArrayFilterUpdate('sources', source.code, e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {source.name}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Ordenar por */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Ordenar por
        </label>
        <select
          value={filters.sortBy || 'relevance'}
          onChange={(e) => handleFilterUpdate('sortBy', e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Limpiar filtros */}
      <button
        onClick={() => onFilterChange({
          searchField: 'all',
          yearFrom: '',
          yearTo: '',
          languages: [],
          sources: [],
          documentTypes: [],
          sortBy: 'relevance'
        })}
        className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        Limpiar filtros
      </button>
    </motion.div>
  );
};

export default FilterPanel;